/**
 * Error tracking, gated entirely on VITE_SENTRY_DSN.
 *
 * Until a DSN is configured this module is completely inert: nothing
 * initialises, nothing is sent, and no network requests are made. That keeps
 * local and CI builds silent while making production crashes visible — until
 * now a white-screen crash was invisible unless a user reported it.
 *
 * Sentry is imported lazily so its ~30KB never lands in the main bundle for
 * builds that don't use it.
 */

const DSN = import.meta.env.VITE_SENTRY_DSN
const ENV = import.meta.env.VITE_APP_MODE || import.meta.env.MODE

let sentry = null
let initPromise = null

export function isErrorTrackingEnabled() {
  return Boolean(DSN)
}

/** Initialise once. Safe (and free) to call when no DSN is set. */
export function initErrorTracking() {
  if (!isErrorTrackingEnabled() || initPromise) return initPromise

  initPromise = import('@sentry/react')
    .then((Sentry) => {
      Sentry.init({
        dsn: DSN,
        environment: ENV,
        // Keep the default integrations only. Session Replay and tracing are
        // deliberately off: this app handles financial data, and replay would
        // capture amounts, names and receipt filenames by default.
        tracesSampleRate: 0,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        // Don't ship a user's email/IP to a third party without a decision to
        // do so; the crash itself is what's useful.
        sendDefaultPii: false,
        beforeSend(event) {
          // Strip the auth token if it ever appears in a URL or breadcrumb.
          if (event.request?.url) {
            event.request.url = event.request.url.replace(/([?&])token=[^&]*/gi, '$1token=[redacted]')
          }
          return event
        },
      })
      sentry = Sentry
      return Sentry
    })
    .catch(() => {
      // Never let a tracking failure break the app.
      initPromise = null
      return null
    })

  return initPromise
}

/**
 * Report a caught error. No-op when tracking is disabled, so callers don't
 * need to guard.
 */
export function reportError(error, context = {}) {
  if (!isErrorTrackingEnabled()) return

  const send = (Sentry) => {
    if (!Sentry) return
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([k, v]) => scope.setContext(k, typeof v === 'object' && v !== null ? v : { value: v }))
      Sentry.captureException(error)
    })
  }

  if (sentry) send(sentry)
  else initErrorTracking()?.then(send)
}
